import React, {useState} from 'react';

import {
  SweetCreatableReactSelect,
  SweetReactSelect,
} from 'app/components/general/SweetReactSelect';
import {useCurrentUserState} from 'app/current-user/api';
import {useLanes} from 'app/lanes/api';
import {gt} from 'app/utils/i18n';

import {LaneSerializedType} from 'sweet-types';

interface Props {
  onChange: (option: LaneSerializedType | null) => void;
  value: LaneSerializedType | null;
  onCreateLane: (name: string) => Promise<unknown>;
}

export const LaneSelect = ({
  onChange,
  value,
  onCreateLane,
}: Props): JSX.Element => {
  const {is_super_manager} = useCurrentUserState().data!;
  const [creating, setCreating] = useState(false);

  const {data, isFetching} = useLanes({});
  const lanes: Array<LaneSerializedType> = data != null ? data.results : [];

  const laneOptions: Array<{value: string; label: string}> = React.useMemo(
    () => lanes.map(({hashid, name}) => ({value: hashid, label: name})),
    [lanes]
  );

  const SelectComponent = is_super_manager
    ? SweetCreatableReactSelect
    : SweetReactSelect;

  const options = {
    value: value != null ? {label: value.name, value: value.hashid} : null,
    isLoading: (lanes.length == 0 && isFetching) || creating,
    isMulti: false,
    placeholder: is_super_manager
      ? lanes.length === 0
        ? gt('Create a lane for this step...')
        : gt('Create or select a step lane...')
      : gt('Select a lane.'),

    noOptionsMessage: (query: string) =>
      query === '' ? (
        is_super_manager ? (
          gt('Create or select a step lane...')
        ) : (
          gt('Select a lane.')
        )
      ) : (
        <>
          {' '}
          <div>{gt('No lanes found.')}</div>{' '}
          {is_super_manager && <div>{gt('Enter the name to create...')}</div>}{' '}
        </>
      ),

    onChange: (value) => {
      if (value == null) {
        return onChange(null);
      }
      let selectedLane: LaneSerializedType | null = null;
      for (const lane of lanes) {
        if (lane.hashid === value.value) {
          selectedLane = lane;
        }
      }
      onChange(selectedLane);
    },

    options: laneOptions,
    isClearable: true,
    autoFocus: true,
    menuIsOpen: data != null,
    filterOption: searchLane,
    hideSelectedOptions: true,
  };
  if (is_super_manager) {
    // onCreateOption and formatCreateLabel only exist in CreatableProps
    options['onCreateOption'] = (name) => {
      setCreating(true);
      return onCreateLane(name).then(
        () => setCreating(false),
        () => setCreating(false)
      );
    };
    options['formatCreateLabel'] = (label: string) => (
      <div className="d-flex">
        <i className="fa fa-plus-circle mr-2 mt-1 flex-grow-0 flex-shrink-0" />
        <div className="flex-grow-1">
          {gt('Create "%s" lane', <b>{label}</b>)}
        </div>
      </div>
    );
  }

  return <SelectComponent<{label: string; value: string}> {...options} />;
};

function searchLane(
  {
    data: {label, __isNew__},
  }: {
    data: {
      label: React.ReactElement | string;
      value: string;
      __isNew__?: boolean;
    };
  },
  query: string
) {
  if (query === '' || label == null || __isNew__ || typeof label !== 'string') {
    return true;
  }

  return label.toLowerCase().indexOf(query.toLowerCase()) > -1;
}
